import type { APIController, AuthenticateData, GameMode } from '../../utils/api.types';
import { getUrlParam } from '@magic/utils';
import { type Balance, type PlayResponse, RGSClient } from 'stake-engine';

export class API implements APIController {
    private debug = false;
    private client = RGSClient({
        url: window.location.href,
    });

    async connect(debug?: boolean): Promise<AuthenticateData> {
        if (debug) {
            this.debug = debug;
        }

        const response = await this.client.Authenticate();
        const gameModes = await this.gameModes();

        if (this.debug) {
            console.log('🕸️ API Authenticated:', { ...response, gameModes });
        }

        return { ...response, gameModes };
    }

    /**
     * The bet modes the RGS offers, including the features it sells — read
     * straight off `/wallet/authenticate`.
     *
     * The stake-engine client can't supply these: it rebuilds the authenticate
     * response field by field (balance, config, jurisdictionFlags, round) and
     * `gameModes` is not among them, so it never reaches the game. The client's
     * own call still has to happen — it caches the bet config `Play` validates
     * against — so this asks for the payload again rather than replacing it.
     *
     * Never fatal: a game whose modes can't be read simply has nothing to sell,
     * which is exactly how a game that sells nothing behaves.
     */
    private async gameModes(): Promise<GameMode[] | undefined> {
        try {
            const response = await fetch(`${this.walletURL()}/wallet/authenticate`, {
                method: 'POST',
                body: JSON.stringify({ sessionID: getUrlParam('sessionID') }),
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(`${response.status}: ${JSON.stringify(data)}`);
            }

            // the RGS has been seen reporting these both at the top level and
            // under `config` — take whichever this one sends
            return (data.gameModes ?? data.config?.gameModes) as GameMode[] | undefined;
        } catch (error) {
            console.error('Failed to read the game modes:', error);
            return undefined;
        }
    }

    async spin(amount: number, mode: string = 'BASE'): Promise<PlayResponse | null> {
        if (!amount) {
            console.error('Bet amount is not set');
            return null;
        }

        try {
            const response = await this.client.Play({
                amount: amount,
                mode,
            });

            if (this.debug) {
                console.log('🕸️ API play:', response, response.round.payout);
            }

            return response;
        } catch (error: unknown) {
            if (error instanceof Error) {
                console.error(error.message);

                if (
                    error.message ===
                    'A round is already active, please call EndRound() before starting a new round'
                ) {
                    try {
                        await this.endRound();
                        return await this.spin(amount, mode);
                    } catch (error) {
                        console.error('Failed to end existing round:', error);
                    }
                }
            }

            return null;
        }
    }

    async endRound() {
        const response = await this.client.EndRound();

        if (this.debug) {
            console.log('🕸️ API endRound:', response);
        }

        return response;
    }

    async event(event: string) {
        const response = await this.client.Event(event);

        if (this.debug) {
            console.log('🕸️ API event:', response);
        }

        return response;
    }

    /**
     * Reads the wallet balance straight from the RGS. The stake-engine client
     * keeps its `/wallet/balance` call private (it only refreshes itself once a
     * minute), so the request is made here to keep the displayed balance in step
     * with wallet changes that happen outside of play — deposits, other tabs,
     * operator adjustments.
     */
    async balance(): Promise<Balance> {
        const response = await fetch(`${this.walletURL()}/wallet/balance`, {
            method: 'POST',
            body: JSON.stringify({ sessionID: getUrlParam('sessionID') }),
            headers: {
                'Content-Type': 'application/json',
            },
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(`Balance request failed (${response.status}): ${JSON.stringify(data)}`);
        }

        if (this.debug) {
            console.log('🕸️ API balance:', data.balance);
        }

        return data.balance as Balance;
    }

    /** The RGS host the session was opened against — the same one the client uses. */
    private walletURL(): string {
        const rgsURL = getUrlParam('rgs_url');

        if (!rgsURL) {
            throw new Error('rgs_url is not set in url parameters');
        }

        return `https://${rgsURL}`;
    }

    /*
      Params:
        decimals: Forces a certain number of decimals, otherwise defaults to a reasonable value based on currency.
        removeSymbol: Removes the currency symbol from the string EG CAD$1.99 -> 1.99
        trimDecimalForIntegers: Any number that is a whole number will not display the decimal points. This may help when displaying bet levels. EG 2.00 -> 2
    */
    // DisplayAmount(balance, {
    //     removeSymbol: true,
    //     decimals: 2,
    //     wholeNumberDecimals: true,
    // });
}

// 400 – Client Errors
// Status Code	Description
// ERR_VAL	Invalid Request
// ERR_IPB	Insufficient Player Balance
// ERR_IS	Invalid Session Token / Session Timeout
// ERR_ATE	Failed User Authentication / Token Expired
// ERR_GLE	Gambling Limits Exceeded
// ERR_LOC	Invalid Player Location

// 500 – Server Errors
// Status Code	Description
// ERR_GEN	General Server Error
// ERR_MAINTENANCE	RGS Under Planned Maintenance
